// The findings list: specific actions ranked by what each costs per month.
//
// Measured over a fixed trailing window rather than the period above, so a
// finding's monthly cost does not move with the date picker. Findings follow
// the table's search and filter, so the two always describe the same products.
// A finding the seller judges intentional can be dismissed and later restored.
import React, { useState } from 'react';

import local from '../ProfitMargin.module.css';
import { EstBadge, Money, SEVERITY_CLASS, formatDate, formatDateTime } from '../format';
import { dismissFinding, restoreFinding } from '../services/profitApi';

const visible = (list, skus) => (skus ? list.filter((f) => !f.sku || skus.has(f.sku)) : list);

const FindingRow = ({ finding, onShowSku, action }) => (
  <div className={`${local.finding} ${finding.severity === 'CRITICAL' ? local.critical : ''}`}>
    <div className={local.findingHead}>
      {finding.impact !== null && (
        <span className={`${local.badge} ${SEVERITY_CLASS[finding.severity] || local.badgeNeutral}`}>
          <Money value={finding.impact} /> / month
        </span>
      )}
      <strong>{finding.title}</strong> <EstBadge show={finding.estimated} />
    </div>
    <p className={local.findingMeta}>
      {finding.headline}.
      {finding.fix && <> <strong>{finding.fix}.</strong></>} {finding.action}
      {finding.sku && onShowSku && (
        <>{' · '}<button type="button" className={local.linkBtn}
          onClick={() => onShowSku(finding.sku)}>show {finding.sku}</button></>
      )}
      {action}
    </p>
  </div>
);

const Findings = ({ token, payload, visibleSkus, onShowSku, onChanged }) => {
  const [showAll, setShowAll] = useState(false);
  const [showUnranked, setShowUnranked] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  if (!payload) return null;
  const ranked = visible(payload.findings ?? [], visibleSkus);
  const unranked = visible(payload.unranked_findings ?? [], visibleSkus);
  const dismissed = payload.dismissed_findings ?? [];
  const shown = payload.findings_default_display || 10;
  if (!ranked.length && !unranked.length && !dismissed.length) return null;

  const change = async (finding, restore) => {
    const key = `${finding.code}-${finding.sku}`;
    setBusy(key);
    setError(null);
    try {
      await (restore ? restoreFinding : dismissFinding)(token, finding.sku, finding.code);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const dismissButton = (finding) => (
    <>{' · '}<button type="button" className={local.linkBtn}
      disabled={busy === `${finding.code}-${finding.sku}`}
      onClick={() => change(finding, false)}>Dismiss</button></>
  );

  return (
    <div className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What to fix first, ranked by what it costs you</h2>
        <span className={local.muted}>
          {ranked.length} findings · <Money value={ranked.reduce((t, f) => t + f.impact, 0)} />{' '}
          a month · measured {formatDate(payload.impact_window?.start)} –{' '}
          {formatDate(payload.impact_window?.end)}
        </span>
      </div>

      {error && <p className={`${local.note} ${local.loss}`}>{error}</p>}

      {ranked.slice(0, showAll ? undefined : shown).map((finding) => (
        <FindingRow key={`${finding.code}-${finding.sku}`} finding={finding}
          onShowSku={onShowSku} action={dismissButton(finding)} />
      ))}
      {ranked.length > shown && (
        <button type="button" className={local.ghost} onClick={() => setShowAll((v) => !v)}>
          {showAll ? `Show top ${shown}` : `Show all ${ranked.length}`}
        </button>
      )}
      {payload.findings_below_floor > 0 && (
        <p className={local.note}>
          {payload.findings_below_floor} smaller findings fall below the impact floor and
          are not listed.
        </p>
      )}

      {unranked.length > 0 && (
        <>
          <button type="button" className={local.linkBtn} onClick={() => setShowUnranked((v) => !v)}>
            {showUnranked ? 'Hide' : 'Show'} {unranked.length} worth a look, with no monthly
            figure to rank them by
          </button>
          {showUnranked && unranked.map((finding) => (
            <FindingRow key={`${finding.code}-${finding.sku}`} finding={finding}
              onShowSku={onShowSku} action={dismissButton(finding)} />
          ))}
        </>
      )}

      {dismissed.length > 0 && (
        <p className={local.note}>
          <button type="button" className={local.linkBtn} onClick={() => setShowDismissed((v) => !v)}>
            {dismissed.length} dismissed
          </button>
        </p>
      )}
      {showDismissed && dismissed.map((finding) => (
        <FindingRow key={`d-${finding.code}-${finding.sku}`} finding={finding}
          action={(
            <>{' · '}Dismissed {finding.dismissed_by ? `by ${finding.dismissed_by} ` : ''}
              {finding.dismissed_at ? formatDateTime(finding.dismissed_at) : ''}{' · '}
              <button type="button" className={local.linkBtn}
                disabled={busy === `${finding.code}-${finding.sku}`}
                onClick={() => change(finding, true)}>Undo</button></>
          )} />
      ))}
    </div>
  );
};

export default Findings;
